/**
 * Locale resolution for the site store.
 *
 * The active locale is owned by @nuxtjs/i18n and is encoded in the *route*, not in a
 * route param. With the `prefix_except_default` strategy the catch-all page resolves to
 * `routepath___en` for `/dates` and `routepath___fr` for `/fr/dates` — `to.params` only
 * ever holds `routepath`. Anything that re-derives the locale must read it from the same
 * place i18n does, or `siteStore.language` drifts away from `useI18n().locale` and the
 * header/footer render in the wrong language.
 */

/** @nuxtjs/i18n's `routesNameSeparator`; `___` is the module default. */
export const I18N_ROUTE_NAME_SEPARATOR = '___'

/** Locale codes are `en` or `pt-BR` shaped. */
const LOCALE_FORMAT = /^([a-z]{2,3})(-[A-Z]{2,3})?$/

export interface ResolveSiteLocaleOptions {
  /** `to.name` of the route being navigated to, e.g. `routepath___fr`. */
  routeName?: unknown
  /** `to.path` of the route being navigated to, e.g. `/fr/dates`. */
  path?: string
  /** Sanitized `?lang=` value, kept as a fallback for routes i18n did not name. */
  queryLang?: string | null
  /** Locale codes the site actually has a configuration for (from Kontent). */
  availableLocales: string[]
  /** Locale to use when nothing else resolves. */
  defaultLocale: string
}

/**
 * Extracts the locale suffix i18n appends to every localized route name
 * (`routepath___fr` -> `fr`). Returns '' for unnamed or non-localized routes.
 */
export function getLocaleFromRouteName(routeName: unknown): string {
  if (typeof routeName !== 'string') return ''

  const index = routeName.lastIndexOf(I18N_ROUTE_NAME_SEPARATOR)
  if (index === -1) return ''

  return routeName.slice(index + I18N_ROUTE_NAME_SEPARATOR.length)
}

/**
 * Reads the locale from the URL prefix (`/fr/dates` -> `fr`). Only a segment that is a
 * known locale counts, so a default-locale path like `/dates` correctly yields ''.
 */
export function getLocaleFromPath(path: string | undefined, availableLocales: string[]): string {
  if (!path) return ''

  const segment = path.split('/')[1] ?? ''
  return availableLocales.includes(segment) ? segment : ''
}

/**
 * Resolves the locale the site store should switch to, preferring the route i18n resolved
 * over anything supplied in the query string. A candidate is only accepted when the site
 * has a configuration for it, so an unsupported locale falls back to the default rather
 * than leaving the store without a `siteConfig`.
 */
export function resolveSiteLocale({
  routeName,
  path,
  queryLang,
  availableLocales,
  defaultLocale
}: ResolveSiteLocaleOptions): string {
  const candidates = [
    getLocaleFromRouteName(routeName),
    getLocaleFromPath(path, availableLocales),
    queryLang ?? ''
  ]

  for (const candidate of candidates) {
    if (!candidate || !LOCALE_FORMAT.test(candidate)) continue
    if (availableLocales.length > 0 && !availableLocales.includes(candidate)) continue

    return candidate
  }

  return defaultLocale
}
